// App-update scheduler: detects that this document is stale and reloads it
// only at a safe moment. Installed once from src/main.tsx.
//
// The service worker keeps skipWaiting + clients.claim, so a new worker takes
// over open tabs immediately while they keep running OLD JavaScript. Applying
// an update is therefore just location.reload(); nothing is sent to the
// worker. Signals only set state (controllerchange only triggers a version
// check). evaluate() is the ONE place that decides, and an automatic reload
// is always deferred to its own timer and re-decided there, so it can never
// fire inside an event handler or a React commit (a hold released during a
// commit would otherwise reload before sibling mount effects had run).
//
// Most people should never notice an update: the page reloads by itself after
// it has sat in the background. The banner is the exception (see
// BANNER_AFTER_STALE_MS in the core).
//
// Policy lives in ./app-update-core.ts (pure, probe-tested); the busy/idle
// registry lives in ./update-safety.ts.

import {
  CHECK_MIN_INTERVAL_MS,
  CHECK_PERIOD_MS,
  CURRENT_STATE,
  DEFAULT_THRESHOLDS,
  DISMISS_KEY,
  RELOAD_GUARD_KEY,
  RETURN_KEY,
  TEST_THRESHOLDS_KEY,
  compareVersion,
  decide,
  parseStoredMark,
  parseVersionInfo,
  resolveThresholds,
  type Decision,
  type RunningBuild,
  type StoredMark,
  type Thresholds,
  type UpdateState,
  type VersionInfo,
} from '@/lib/app-update-core';
import { getReleaseCapabilities } from '@/lib/release-capabilities';
import {
  applyUpdateNow,
  installUpdateSafety,
  isHardBusy,
  isIdleForSilentApply,
  isTypingRecently,
  isVoiceWorkActive,
  subscribeUpdateSafety,
} from '@/lib/update-safety';

// Injected by the whw-app-version plugin in vite.config.ts. The same value is
// written to /version.json, so equality means "same build" even when
// VITE_BUILD_ID is unset. Absent under plain-Node probes and other configs.
declare const __WHW_BUILT_AT__: number | undefined;

const RUNNING: RunningBuild = {
  buildId: getReleaseCapabilities().buildId,
  builtAt: typeof __WHW_BUILT_AT__ === 'number' ? __WHW_BUILT_AT__ : 0,
};

/** Re-evaluation cadence while stale (predicates without events of their own). */
const TICK_BANNER_MS = 2_000;
const TICK_QUIET_MS = 30_000;
/** If a reload did not unload the page, allow another decision after this. */
const APPLY_STUCK_MS = 10_000;
/** An automatic reload is re-decided on its own timer after this delay. */
const APPLY_DEFER_MS = 250;
/**
 * "Back after a long absence, before touching anything" only holds for a
 * moment. After this window a return counts as an interacting user, even
 * without pointer or key input (hardware back, edge swipe, wheel).
 */
const RETURN_APPLY_WINDOW_MS = 5_000;
/**
 * "Finishing…" is for a save that is about to complete. If the app is still
 * busy after this long, the line steps aside until it is not.
 */
const FINISHING_MAX_MS = 4_000;
/** A saved return position is only honoured this soon after the reload. */
const RETURN_MAX_AGE_MS = 60_000;
const RETURN_RETRY_MS = [0, 80, 200, 400, 700, 1100, 1700, 2500];

export interface AppUpdateSnapshot {
  /** A banner should be offered (the component may still hide it). */
  visible: boolean;
  dismissible: boolean;
  forced: boolean;
  /** Briefly busy: Refresh is disabled and reads "Finishing…". */
  busy: boolean;
  /** The user typed within the last few seconds. */
  typing: boolean;
}

const HIDDEN_SNAPSHOT: AppUpdateSnapshot = Object.freeze({
  visible: false,
  dismissible: true,
  forced: false,
  busy: false,
  typing: false,
});

let uninstall: (() => void) | null = null;
let registration: ServiceWorkerRegistration | null = null;
let hadController = false;
let state: UpdateState = CURRENT_STATE;
/** A signal fired that /version.json has not yet confirmed or cleared. */
let pendingSignal: string | null = null;
/** A chunk failed to load in this document. */
let chunkFailed = false;
let hiddenSince: number | null = null;
let lastHiddenMs = 0;
let inputSinceVisible = true;
let returnApplyUntil = 0;
let lastCheckAt = 0;
let checkSeq = 0;
let applying = false;
let busySince: number | null = null;
let tickTimer: ReturnType<typeof setTimeout> | undefined;
let applyTimer: ReturnType<typeof setTimeout> | undefined;
/** Fallback when sessionStorage refuses the dismiss mark. */
let memoryDismissed = false;
let snapshot: AppUpdateSnapshot = HIDDEN_SNAPSHOT;
/** The defaults everywhere except a test host (see resolveThresholds). */
let thresholds: Thresholds = DEFAULT_THRESHOLDS;
const subscribers = new Set<() => void>();

function readSession(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeSession(key: string, value: string): boolean {
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

function removeSession(key: string): void {
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Nothing to forget.
  }
}

function readMark(key: string): StoredMark | null {
  return parseStoredMark(readSession(key));
}

function publish(next: AppUpdateSnapshot): void {
  const same =
    next.visible === snapshot.visible &&
    next.dismissible === snapshot.dismissible &&
    next.forced === snapshot.forced &&
    next.busy === snapshot.busy &&
    next.typing === snapshot.typing;
  if (same) return;
  snapshot = next.visible ? next : HIDDEN_SNAPSHOT;
  for (const subscriber of [...subscribers]) subscriber();
}

function scheduleTick(): void {
  if (tickTimer !== undefined) {
    clearTimeout(tickTimer);
    tickTimer = undefined;
  }
  if (!state.stale || uninstall === null) return;
  tickTimer = setTimeout(evaluate, snapshot.visible ? TICK_BANNER_MS : TICK_QUIET_MS);
}

/**
 * The line as the user should see it. Recording and transcription hide it
 * outright (never interrupt a spoken prayer); any other busy moment shows
 * "Finishing…" briefly and then steps aside.
 */
function bannerSnapshot(decision: Decision, now: number): AppUpdateSnapshot {
  if (decision.action !== 'banner' || isVoiceWorkActive()) {
    busySince = null;
    return HIDDEN_SNAPSHOT;
  }
  const busy = isHardBusy();
  if (!busy) busySince = null;
  else if (busySince === null) busySince = now;
  if (busy && busySince !== null && now - busySince >= FINISHING_MAX_MS) {
    return HIDDEN_SNAPSHOT;
  }
  return {
    visible: true,
    dismissible: decision.dismissible,
    forced: state.forced,
    busy,
    typing: isTypingRecently(now),
  };
}

interface ReturnPosition {
  path: string;
  y: number;
  draftY: number;
  /** Composer text length and cursor, or -1 when there is no composer. */
  draftLength: number;
  selectionStart: number;
  selectionEnd: number;
  at: number;
}

/**
 * Remember where the user is so the fresh document can put them back: page,
 * scroll, and the cursor in the draft-backed composer. Positions only, never
 * the text itself (the composer's own draft carries that).
 */
function saveReturnPosition(): void {
  const draft = document.querySelector<HTMLElement>('[data-update-safe-draft]');
  const composer = draft instanceof HTMLTextAreaElement ? draft : null;
  const position: ReturnPosition = {
    path: window.location.pathname + window.location.search,
    y: Math.round(window.scrollY),
    draftY: draft ? Math.round(draft.scrollTop) : 0,
    draftLength: composer ? composer.value.length : -1,
    selectionStart: composer ? composer.selectionStart : -1,
    selectionEnd: composer ? composer.selectionEnd : -1,
    at: Date.now(),
  };
  writeSession(RETURN_KEY, JSON.stringify(position));
}

/**
 * After an update reload, return to the same scroll position on the same
 * page, and put the cursor back in the composer once its draft text is
 * restored. The chapter reader restores its own reading position, so it is
 * left alone. Stops the moment the user scrolls, taps or types.
 */
function restoreReturnPosition(): () => void {
  const raw = readSession(RETURN_KEY);
  if (raw === null) return () => undefined;
  // Reading it once is enough; a stale mark is ignored by its age.
  removeSession(RETURN_KEY);
  let saved: ReturnPosition;
  try {
    const value = JSON.parse(raw) as Record<string, unknown>;
    const integer = (field: unknown): number =>
      typeof field === 'number' && Number.isInteger(field) ? field : -1;
    if (
      typeof value.path !== 'string' ||
      typeof value.y !== 'number' ||
      typeof value.draftY !== 'number' ||
      typeof value.at !== 'number'
    ) {
      return () => undefined;
    }
    saved = {
      path: value.path,
      y: value.y,
      draftY: value.draftY,
      draftLength: integer(value.draftLength),
      selectionStart: integer(value.selectionStart),
      selectionEnd: integer(value.selectionEnd),
      at: value.at,
    };
  } catch {
    return () => undefined;
  }
  if (Date.now() - saved.at > RETURN_MAX_AGE_MS) return () => undefined;
  if (saved.path !== window.location.pathname + window.location.search) return () => undefined;
  const ownsScroll = /^\/bible\/[^/]+\/[1-9]\d*$/.test(window.location.pathname);

  let cancelled = false;
  let cursorRestored = false;
  const cancel = () => {
    cancelled = true;
  };
  const passive: AddEventListenerOptions = { capture: true, passive: true };
  const inputs = ['pointerdown', 'wheel', 'touchstart', 'keydown'] as const;
  for (const type of inputs) window.addEventListener(type, cancel, passive);
  const timers = RETURN_RETRY_MS.map((delay) =>
    setTimeout(() => {
      if (cancelled) return;
      if (!ownsScroll && saved.y > 0 && Math.abs(window.scrollY - saved.y) > 2) {
        window.scrollTo(0, saved.y);
      }
      const draft = document.querySelector<HTMLElement>('[data-update-safe-draft]');
      if (!draft) return;
      // The cursor goes back only once the composer holds the same amount of
      // text it had, so it can never land in different words.
      if (
        !cursorRestored &&
        draft instanceof HTMLTextAreaElement &&
        saved.selectionStart >= 0 &&
        saved.selectionEnd >= saved.selectionStart &&
        saved.draftLength > 0 &&
        draft.value.length === saved.draftLength
      ) {
        cursorRestored = true;
        try {
          draft.setSelectionRange(saved.selectionStart, saved.selectionEnd);
        } catch {
          // Not selectable right now; the scroll position below still applies.
        }
      }
      if (saved.draftY > 0 && Math.abs(draft.scrollTop - saved.draftY) > 2) {
        draft.scrollTop = saved.draftY;
      }
    }, delay),
  );
  return () => {
    cancelled = true;
    for (const timer of timers) clearTimeout(timer);
    for (const type of inputs) window.removeEventListener(type, cancel, passive);
  };
}

function apply(manual: boolean): void {
  if (applying || state.target === null) return;
  const prior = readMark(RELOAD_GUARD_KEY);
  const spent = prior !== null && prior.target === state.target ? (prior.count ?? 1) : 0;
  const guarded = writeSession(
    RELOAD_GUARD_KEY,
    JSON.stringify({
      target: state.target,
      at: Date.now(),
      // Only automatic reloads spend the per-target budget.
      count: manual ? spent : spent + 1,
    }),
  );
  // Without a working loop guard an automatic reload could repeat forever;
  // a tap is an explicit, one-off request and is always honoured.
  if (!guarded && !manual) {
    publish(bannerSnapshot({ action: 'banner', dismissible: !state.forced }, Date.now()));
    scheduleTick();
    return;
  }
  applying = true;
  setTimeout(() => {
    applying = false;
    evaluate();
  }, APPLY_STUCK_MS);
  saveReturnPosition();
  applyUpdateNow();
}

function currentDecision(now: number): Decision {
  return decide(
    state,
    {
      now,
      online: navigator.onLine !== false,
      visible: document.visibilityState === 'visible',
      hiddenSince,
      lastHiddenMs,
      inputSinceVisible: inputSinceVisible || now > returnApplyUntil,
      idle: state.stale ? isIdleForSilentApply(now) : false,
      reloadGuard: readMark(RELOAD_GUARD_KEY),
      dismissed: memoryDismissed || readSession(DISMISS_KEY) !== null,
    },
    thresholds,
  );
}

function evaluate(): void {
  if (uninstall === null || applying) return;
  const now = Date.now();
  const decision = currentDecision(now);
  if (decision.action === 'apply') {
    // Never reload from this call stack. Re-decide on a fresh timer: by then
    // any React commit that triggered this has finished (draft flushers are
    // registered), and a condition that changed in between cancels it.
    if (applyTimer === undefined) {
      applyTimer = setTimeout(() => {
        applyTimer = undefined;
        if (uninstall === null || applying) return;
        if (currentDecision(Date.now()).action === 'apply') apply(false);
        else evaluate();
      }, APPLY_DEFER_MS);
    }
    return;
  }
  publish(bannerSnapshot(decision, now));
  scheduleTick();
}

async function fetchVersionInfo(): Promise<VersionInfo | null> {
  try {
    const response = await fetch('/version.json', { cache: 'no-store' });
    if (!response.ok) return null;
    return parseVersionInfo(response.headers.get('content-type'), await response.text());
  } catch {
    return null;
  }
}

/**
 * One check = registration.update() + GET /version.json. No-op without a
 * registration (unsupported, or localhost where index.html skips register)
 * and while offline.
 */
async function runCheck(force: boolean): Promise<void> {
  if (registration === null || uninstall === null) return;
  if (navigator.onLine === false) return;
  const now = Date.now();
  if (!force && now - lastCheckAt < CHECK_MIN_INTERVAL_MS) return;
  lastCheckAt = now;
  const seq = ++checkSeq;
  registration.update().catch(() => undefined);
  const info = await fetchVersionInfo();
  if (seq !== checkSeq || uninstall === null) return;
  const since = state.stale ? state.since : Date.now();
  if (info !== null) {
    pendingSignal = null;
    const result = compareVersion(RUNNING, info);
    state = result.stale
      ? {
          stale: true,
          confirmed: true,
          forced: result.forced,
          target: result.target,
          since,
          urgent: chunkFailed,
        }
      : CURRENT_STATE;
  } else if (pendingSignal !== null) {
    // A signal fired but the server gave no usable answer. Never reload
    // automatically on an unconfirmed signal.
    if (!state.confirmed) {
      state = {
        stale: true,
        confirmed: false,
        forced: false,
        target: pendingSignal,
        since,
        urgent: chunkFailed,
      };
    }
    pendingSignal = null;
  }
  evaluate();
}

function signal(reason: string): void {
  pendingSignal = reason;
  void runCheck(true);
}

/** Reactive banner state (useSyncExternalStore). */
export function subscribeAppUpdate(subscriber: () => void): () => void {
  subscribers.add(subscriber);
  return () => {
    subscribers.delete(subscriber);
  };
}

export function getAppUpdateSnapshot(): AppUpdateSnapshot {
  return snapshot;
}

/** The Refresh button. Ignored while hard-busy. */
export function requestAppUpdateApply(): void {
  if (!state.stale || isHardBusy()) {
    evaluate();
    return;
  }
  apply(true);
}

/** Hide the banner for the rest of this tab session (required updates still show). */
export function dismissAppUpdate(): void {
  if (!state.stale || state.forced) return;
  if (!writeSession(DISMISS_KEY, '1')) memoryDismissed = true;
  evaluate();
}

/**
 * Idempotent (StrictMode / repeated calls return the same disposer). The
 * disposer removes every listener and timer.
 */
export function installAppUpdate(): () => void {
  if (uninstall !== null) return uninstall;
  if (typeof window === 'undefined') return () => undefined;

  const disposers: Array<() => void> = [];
  const listen = (
    target: EventTarget,
    type: string,
    handler: EventListener,
    options?: AddEventListenerOptions,
  ) => {
    target.addEventListener(type, handler, options);
    disposers.push(() => target.removeEventListener(type, handler, options));
  };

  const dispose = () => {
    if (uninstall !== dispose) return;
    uninstall = null;
    for (const undo of disposers.splice(0)) undo();
    if (tickTimer !== undefined) clearTimeout(tickTimer);
    tickTimer = undefined;
    if (applyTimer !== undefined) clearTimeout(applyTimer);
    applyTimer = undefined;
    registration = null;
  };
  uninstall = dispose;

  // Test hosts only: shortened waiting periods from the URL flag. On every
  // public domain this is always the defaults and removes a remembered value.
  const resolved = resolveThresholds(
    window.location.hostname,
    window.location.search,
    readSession(TEST_THRESHOLDS_KEY),
  );
  if (resolved.clear) removeSession(TEST_THRESHOLDS_KEY);
  if (resolved.store !== null) writeSession(TEST_THRESHOLDS_KEY, resolved.store);
  thresholds = resolved.thresholds;

  // Put the user back where they were if this document is the result of an
  // update reload. Independent of service-worker support.
  disposers.push(restoreReturnPosition());

  if (!('serviceWorker' in navigator)) return dispose;

  disposers.push(installUpdateSafety());
  disposers.push(subscribeUpdateSafety(evaluate));

  // A controller at boot means a later controllerchange is a real takeover
  // by a newer worker. The first-ever install also fires controllerchange
  // (clients.claim); that one is not an update.
  hadController = navigator.serviceWorker.controller !== null;
  listen(navigator.serviceWorker, 'controllerchange', () => {
    if (!hadController) {
      hadController = true;
      return;
    }
    // Never reload here. A cold open after a deploy already runs the new
    // build while the old worker still controls it, so this event alone does
    // not mean "stale"; /version.json decides.
    signal('controller');
  });

  // A hashed chunk failed to load: this document may be older than what the
  // server and caches still hold, and something just failed for the user, so
  // the line is offered at once. Not preventDefault-ed; the import's own
  // error handling still runs.
  listen(window, 'vite:preloadError', () => {
    chunkFailed = true;
    signal('chunk');
  });

  // A document that starts in the background (restored tab, background open)
  // is hidden from now, so its absence is measured too.
  if (document.visibilityState === 'hidden') hiddenSince = Date.now();

  const onVisible = () => {
    if (hiddenSince !== null) {
      const now = Date.now();
      lastHiddenMs = now - hiddenSince;
      hiddenSince = null;
      inputSinceVisible = false;
      returnApplyUntil = now + RETURN_APPLY_WINDOW_MS;
    }
    // Decide on what is already known before the user can interact, then
    // refresh the knowledge.
    evaluate();
    void runCheck(pendingSignal !== null);
  };
  listen(document, 'visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      hiddenSince = Date.now();
      scheduleTick();
      return;
    }
    onVisible();
  });
  listen(window, 'pageshow', ((event: PageTransitionEvent) => {
    if (event.persisted) onVisible();
  }) as EventListener);
  listen(window, 'focus', () => void runCheck(pendingSignal !== null));
  listen(window, 'online', () => {
    evaluate();
    void runCheck(true);
  });
  listen(window, 'offline', evaluate);

  const markInput = () => {
    inputSinceVisible = true;
  };
  const passive: AddEventListenerOptions = { capture: true, passive: true };
  listen(window, 'pointerdown', markInput, passive);
  listen(window, 'keydown', markInput, passive);
  listen(window, 'touchstart', markInput, passive);
  listen(window, 'wheel', markInput, passive);
  // Hardware back, the browser back button and an edge swipe close overlays
  // without any pointer or key event reaching the page.
  listen(window, 'popstate', markInput);

  const period = setInterval(() => {
    if (document.visibilityState === 'visible') void runCheck(false);
  }, CHECK_PERIOD_MS);
  disposers.push(() => clearInterval(period));

  navigator.serviceWorker.ready
    .then((ready) => {
      if (uninstall !== dispose) return;
      registration = ready;
      void runCheck(true);
    })
    .catch(() => undefined);

  return dispose;
}
